import React, { useState, useEffect } from 'react';

import { Popover, Input, notification } from 'antd';

import styled from 'styled-components';

import { isBookingEvent } from '@libs/EventTypes';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useCancelEvent, useCancelEventDateModel } from '@hooks/API/Event/UseEvent';
import { useDeleteEventDatesModels } from '@hooks/API/Event/UseEvent';
import { useCancelExternalJob } from '@hooks/API/Event/UseExternalJob';
import { useCancelFollowup } from '@hooks/API/Event/UseFollowup';
import { useCancelMeeting } from '@hooks/API/Event/UseMeeting';
import { useCancelScoutingTravel } from '@hooks/API/Event/UseScoutingTravel';
import { useCancelTravel } from '@hooks/API/Event/UseTravel';

import MiniLoading from '@components/Gui/MiniLoading';
import { NwButton, NwRemoveLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CancellationWrapper = styled.div`
    width: 240px;

    .ant-btn {
        border-radius: 6px !important;
        //border-right: 1px solid ${(props) => props.theme.LighterBorderColor} !important;
        &.ant-btn-primary:hover {
            color: #fff;
        }
        &.ant-btn-link.ant-btn-dangerous {
            &:hover {
                border: none;
                color: ${(props) => props.theme.DangerColor};
            }
        }
    }
`;

const CancelEvent = ({
    eventId,
    eventData,
    button,
    type,
    previewMode,
    // eventDateInfo,
    // scoutingTravel,
    cancelValue,
    modelID,
    eventDateModelID,
    onStatusChanged,
}) => {
    const [isLoading, setLoading] = useState(false);
    const [cancel, setCancel] = useState(false);
    const { mutateAsync: onCancelMeeting } = useCancelMeeting();
    const { mutateAsync: onCancelTravel } = useCancelTravel();
    const { mutateAsync: onCancelFollowup } = useCancelFollowup();
    const { mutateAsync: onCancelExternalJob } = useCancelExternalJob();
    const { mutateAsync: onCancelEvent } = useCancelEvent();
    const { mutateAsync: onCancelEventDateModel } = useCancelEventDateModel();
    const { mutateAsync: onCancelScoutingTravel } = useCancelScoutingTravel();
    const [customerType, setCustomerType] = useState('Client');
    const [notes, setNotes] = useState('');
    const [visible, setVisible] = useState(false);
    const { mutateAsync: onDeleteEventDatesModels } = useDeleteEventDatesModels();

    useEffect(() => {
        setCancel(cancelValue);
    }, [cancelValue]);

    const handleRemoveDate = async () => {
        try {
            await onDeleteEventDatesModels({
                ID: eventData.ID,
                eventDateModelId: eventDateModelID,
                projectID: eventData.Project.ID,
                force: false,
            });
            onStatusChanged();
        } catch (error) {
            console.error('Remove Event Date Model Error => ', error);

            const key = 'force-delete';
            const forceRemoveDate = async () => {
                try {
                    await onDeleteEventDatesModels({
                        ID: eventData.ID,
                        eventDateModelId: eventDateModelID,
                        projectID: eventData.Project.ID,
                        force: true,
                    });
                    notification.close(key);
                    onStatusChanged();
                } catch (error) {
                    console.error('Remove Event Date Model Error => ', error);
                }
            };

            if (error.errorCode === 'DateWithLockedTransactions') {
                notification.error({
                    duration: 0,
                    message: 'Error',
                    description:
                        'We’re sorry, but you cannot delete this job date because there are invoiced or paid expenses or fees. Please review the linked expenses or fees before attempting to modify this entry.',
                    icon: <NwIcon icon={byPrefixAndName.fass['exclamation-circle']} />,
                    key,
                });
                setVisible(false);
            }

            if (error.errorCode === 'DateWithTransactions') {
                const btn = <NwButton primary onClick={forceRemoveDate} label="Force Delete" />;

                notification.warning({
                    duration: 0,
                    message: 'Warning',
                    description:
                        'We’re sorry, but you cannot delete this job date because there are associated expenses or fees. Click the button below to force the deletion, removing the job date and all associated expenses or fees.',
                    icon: <NwIcon icon={byPrefixAndName.fass['exclamation-circle']} />,
                    btn,
                    key,
                });
                setVisible(false);
            }
        }
    };

    useEffect(() => {
        return function cleanup() {};
    });

    const onCancelEvents = async () => {
        let endpoint = '';

        switch (type.toLowerCase()) {
            case 'extjob':
                await onCancelExternalJob({ ID: eventId });
                break;
            case 'scoutingtravel':
                await onCancelScoutingTravel({ ID: eventId });
                break;
            case 'travel':
                await onCancelTravel({ ID: eventId });
                break;
            case 'followup':
                await onCancelFollowup({ ID: eventId });
                break;
            case 'meeting':
                await onCancelMeeting({ ID: eventId });
                break;
            case 'event':
                await onCancelEvent({ ID: eventId, cancel: !cancelValue });
                break;
            case 'casting':
            case 'job':
            case 'ext':
                await onCancelEventDateModel({
                    EventDateModelsIDs: [eventDateModelID],
                    Cancel: {
                        Cancel: !cancelValue,
                        CancelledBy: !cancelValue ? customerType : '',
                        CancellationNote: cancelValue ? '' : notes,
                    },
                    eventId: eventId,
                });
                setCustomerType('Client');
                setNotes('');
                break;
            default:
                return null;
        }
        return endpoint;
    };

    const toggleCancel = async (e) => {
        if (!previewMode) {
            setLoading(true);
            e.stopPropagation();
            try {
                const data = await onCancelEvents();
                if (onStatusChanged) {
                    onStatusChanged();
                }
                setCancel(data);
                setLoading(false);
                setVisible(false);
            } catch (error) {
                console.error('error', error);
                setLoading(false);
            }
        }
    };

    const getStyleIcon = { fontSize: '1.5rem', opacity: cancel ? '1' : '.25' };
    //const getStyleButton = { backgroundColor: cancel ? StyleVariables.DangerColor : "inherit", color: cancel ? "#fff" : "inherit", borderColor: cancel ? StyleVariables.DangerColor : StyleVariables.DarkerBorder }
    const color = cancel ? StyleVariables.DangerColor : StyleVariables.GreyedColor;

    return button ? (
        isBookingEvent(type) ? (
            !cancelValue ? (
                <Popover
                    overlayClassName="nw-popover"
                    trigger={'click'}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    onClick={(e) => e.stopPropagation()}
                    onOpenChange={setVisible}
                    open={visible}
                    placement="left"
                    content={
                        <CancellationWrapper onClick={(e) => e.stopPropagation()}>
                            <div>Cancelled by</div>
                            <RadioButtonGroup
                                value={customerType}
                                onChange={setCustomerType}
                                size="small"
                                options={[
                                    { label: 'Client', value: 'Client' },
                                    { label: 'Agency', value: 'Agency' },
                                    { label: 'Model', value: 'Model' },
                                ]}
                            />
                            <div style={{ marginTop: '1rem' }}>
                                <small>NOTES ABOUT CANCELLATION</small>
                                <Input autoComplete="off" value={notes} onChange={(e) => setNotes(e.target.value)} />
                            </div>
                            <div style={{ marginTop: '1.5rem' }}>
                                <NwButton primary size="small" onClick={toggleCancel} label="Save" />
                            </div>
                            {modelID && eventData && (
                                <div style={{ marginTop: '2rem' }}>
                                    <NwDivider>DELETE</NwDivider>
                                    <div>
                                        or &nbsp;&nbsp;&nbsp;
                                        <NwRemoveLinkButton
                                            onClick={handleRemoveDate}
                                            needConfirmation={false}
                                            label="Delete Permanently"
                                        />
                                    </div>
                                    <div>{`${getContactName(modelID)} for this date`}</div>
                                </div>
                            )}
                        </CancellationWrapper>
                    }
                >
                    <NwButton
                        loading={isLoading}
                        icon={byPrefixAndName.fass['times']}
                        iconOpacity={cancel ? 1 : 0.25}
                        className={cancel ? 'event-cancelled-button' : ''}
                        //danger={cancel}
                    />
                </Popover>
            ) : (
                <NwButton
                    loading={isLoading}
                    icon={byPrefixAndName.fass['times']}
                    onClick={toggleCancel}
                    iconOpacity={cancel ? 1 : 0.25}
                    className={cancel ? 'event-cancelled-button' : ''}
                    //style={getStyleButton}
                    //danger={cancel}
                />
            )
        ) : (
            <NwButton
                loading={isLoading}
                icon={byPrefixAndName.fass['times']}
                onClick={toggleCancel}
                iconOpacity={cancel ? 1 : 0.25}
                className={cancel ? 'event-cancelled-button' : ''}
                //style={getStyleButton}
                //danger={cancel}
            />
        )
    ) : isLoading ? (
        <MiniLoading />
    ) : (
        <NwIcon onClick={toggleCancel} style={getStyleIcon} color={color} icon={byPrefixAndName.fass['times']} />
    );
};
export default CancelEvent;
